import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Acknowledge every open critical event at once, optionally only some
 * types. A note is required — it's saved against each event and is the only
 * record of why a batch of critical alerts was dismissed. Only events up to
 * `upToSeq` (the newest one the admin was shown) are covered, so anything
 * that arrives while this is open stays open.
 */
const BulkAcknowledgeDialog = ({ open, onOpenChange, byAction, upToSeq, onDone }) => {
  const [chosen, setChosen] = useState(() => new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Every type ticked each time it opens.
  useEffect(() => {
    if (open) {
      setChosen(new Set((byAction || []).map((t) => t.action)));
      setNote('');
    }
  }, [open, byAction]);

  const total = useMemo(
    () => (byAction || []).filter((t) => chosen.has(t.action)).reduce((n, t) => n + t.count, 0),
    [byAction, chosen],
  );
  const everyType = chosen.size === (byAction || []).length;
  const ready = total > 0 && note.trim().length >= 3 && !saving;

  const toggle = (action) => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(action)) next.delete(action); else next.add(action);
    return next;
  });

  const submit = () => {
    setSaving(true);
    adminApi
      .acknowledgeAllSecurityEvents({
        up_to_seq: upToSeq,
        actions: everyType ? null : [...chosen],
        note: note.trim(),
      })
      .then((r) => {
        toast.success(`Acknowledged ${plural(r.data?.acknowledged ?? total, 'event')}.`);
        onOpenChange(false);
        onDone();
      })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not acknowledge these events'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="bulk-ack-dialog">
        <DialogHeader>
          <DialogTitle>Acknowledge these critical events?</DialogTitle>
          <DialogDescription>
            Untick any type you haven't looked into yet. Anything that arrives while this is open
            stays open.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1 max-h-[40vh] overflow-y-auto" aria-label="Open critical events by type">
          {(byAction || []).map((t) => (
            <li key={t.action}>
              <label className="flex items-center gap-3 rounded-md px-2 py-2.5 hover:bg-ground cursor-pointer">
                <Checkbox
                  checked={chosen.has(t.action)}
                  onCheckedChange={() => toggle(t.action)}
                  aria-label={`${t.label}, ${plural(t.count, 'event')}`}
                  data-testid={`bulk-ack-type-${t.action}`}
                />
                <span className="flex-1 text-ink">{t.label}</span>
                <span className="pd-mono text-ink-55">× {t.count}</span>
              </label>
            </li>
          ))}
        </ul>

        <div className="space-y-2">
          <label htmlFor="bulk-ack-note" className="text-[15px] font-semibold text-ink block">
            Why are you acknowledging these?
          </label>
          <p className="text-sm text-ink-55">
            Required. Saved with your name against every event, which is never itself changed.
          </p>
          <Textarea
            id="bulk-ack-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            rows={2}
            placeholder="e.g. All from provisioning on 24 September"
            data-testid="bulk-ack-note"
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!ready} data-testid="bulk-ack-submit">
            {saving ? 'Acknowledging…' : `Acknowledge ${plural(total, 'event')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAcknowledgeDialog;
